<template>
  <div v-if="show" class="candidate-questionnaire-container" :class="[hasResumeView && !onlyOpenQuestion && 'has-resume-view', qrImage && 'qrImage-container']">
    <template v-if="qrImage">
      <el-card class="qrcode-card">
        <div class="qrcode-container">
          <img class="qrcode" :src="qrImage">
        </div>
      </el-card>
      <div class="qrcode-tip">{{ $t(`${i18nPrefix}.label.elcQuestionQRCodeTip`) }}</div>
    </template>
    <template v-else>
      <Question
        v-for="(question) in stepData"
        v-model="question.answer"
        :ref="`q_${question.id}`"
        :key="question.id"
        :type="question.type"
        :title="question.title"
        :surveyType="question.surveyType"
        :isRequired="question.isRequired"
        :optionList="question.optionList"
        :uploaderNeedFileDescription="question.uploaderNeedFileDescription"
        :uploaderFileDescription.sync="question.uploaderFileDescription"
        :uploaderFileTypeList="question.uploaderFileTypeList"
        :uploaderFileSize="question.uploaderFileSize"
        :uploaderTipList="question.uploaderTipList"
        :policyLink="question.policyLink"
        :errorMessage="question.errorMessage"
        @change="handleQuestionAnswerChange(question, $event)"
        @file-upload="handleFileUpload(question, ...arguments)"
      />
      <RectLoading v-show="loading" class="loading"></RectLoading>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'
import mixin from './mixin'
import Question, { QUESTION_TYPE } from './question.vue'
import { QUESTION_TYPE as OPEN_QUESTION_TYPE } from '@/constant/openQuestion'
import { ONBOARDING_QUESTION_TYPE } from '@/constant/common'
import { QUESTIONNAIRE_QUESTION_TYPE } from '@/constant/questionnaire'
import { addI18nProperty } from '@/i18n'
import { uploadFile, getFileUID } from '@/utils/qiniu'
import { mapActions, mapGetters } from 'vuex'
import RectLoading from '@/components/loading/rectLoading.vue'
import moment from 'moment'

export const QUESTIONNAIRE_TYPE = {
  OPEN_QUESTION: 'OPEN_QUESTION',
  ONBOARDING: 'ONBOARDING'
}

const OPEN_QUESTION_TYPE_MAP = {
  [OPEN_QUESTION_TYPE.VIDEO]: QUESTION_TYPE.VIDEO,
  [OPEN_QUESTION_TYPE.ATTACHMENT]: QUESTION_TYPE.ATTACHMENT,
  [OPEN_QUESTION_TYPE.SELECT]: QUESTION_TYPE.RADIO,
  [OPEN_QUESTION_TYPE.CHECKBOX]: QUESTION_TYPE.CHECKBOX,
  [OPEN_QUESTION_TYPE.TEXT]: QUESTION_TYPE.TEXTAREA,
  [OPEN_QUESTION_TYPE.SINGLE_SELECT]: QUESTION_TYPE.SELECT,
  [OPEN_QUESTION_TYPE.MULTIPLE_SELECT]: QUESTION_TYPE.MULTIPLE,
  [OPEN_QUESTION_TYPE.DATE]: QUESTION_TYPE.DATE
}

const ONBOARDING_QUESTION_TYPE_MAP = {
  [QUESTION_TYPE.TEXT]: [
    ONBOARDING_QUESTION_TYPE.TEXT.value,
    QUESTIONNAIRE_QUESTION_TYPE.DATE_RANGE.value,
    QUESTIONNAIRE_QUESTION_TYPE.REPEAT_GROUP.value
  ],
  [QUESTION_TYPE.SELECT]: [
    ONBOARDING_QUESTION_TYPE.SINGLE.value,
    ONBOARDING_QUESTION_TYPE.LOCATION.value,
    ONBOARDING_QUESTION_TYPE.NATIONALITY.value,
    ONBOARDING_QUESTION_TYPE.COUNTRY.value,
    ONBOARDING_QUESTION_TYPE.PROVINCE.value,
    ONBOARDING_QUESTION_TYPE.CITY.value,
    ONBOARDING_QUESTION_TYPE.LANGUAGE.value
  ],
  [QUESTION_TYPE.DATE]: [ONBOARDING_QUESTION_TYPE.DATE.value],
  [QUESTION_TYPE.ATTACHMENT]: [ONBOARDING_QUESTION_TYPE.ATTACHMENT.value],
  [QUESTION_TYPE.POLICY]: [ONBOARDING_QUESTION_TYPE.LINK.value]
}

const MOBILE_OPEN_QUESTION_PATH = 'candidate-job-survey/'
const MOBILE_INVITE_QUESTION_PATH = 'candidate-invite-job-survey/'

export default {
  mixins: [mixin],
  components: {
    Question,
    RectLoading
  },
  props: {
    infoPropName: {
      type: String,
      required: true
    },
    showQrImagePropName: {
      type: String,
      default: null
    },
    questionnaireType: {
      type: String,
      default: QUESTIONNAIRE_TYPE.OPEN_QUESTION
    },
    hasResumeView: {
      type: Boolean,
      default: false
    },
    jobId: {
      type: String,
      default: null
    },
    onlyOpenQuestion: {
      type: Boolean,
      default: false
    },
    setting: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      hasForm: false,
      stepData: {},
      qrImage: null,
      account: null
    }
  },
  computed: {
    isCandidate () {
      return Boolean(this.setting?.isCandidate)
    }
  },
  created () {
    this.account = this.getCurrentAccount()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('application', ['handleOpenQuestionUploadedVideo', 'getOpenQuestionUploadedVideoDuration']),
    formatOpenQuestion (questionList) {
      const vm = this
      return _.map(questionList, question => {
        const openQuestionType = question.type
        const questionType = OPEN_QUESTION_TYPE_MAP[openQuestionType]
        let answer = question.answer_value
        let uploaderFileTypeList = null
        const uploaderFileSize = 200 * 1024 * 1024
        let uploaderFileDescription = null
        if (openQuestionType === OPEN_QUESTION_TYPE.SELECT || openQuestionType === OPEN_QUESTION_TYPE.SINGLE_SELECT) {
          answer = answer && parseInt(answer)
        } else if (openQuestionType === OPEN_QUESTION_TYPE.CHECKBOX || openQuestionType === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
          answer = _.chain(answer).split(',').map(item => parseInt(item)).filter().value()
        } else if (openQuestionType === OPEN_QUESTION_TYPE.VIDEO) {
          uploaderFileTypeList = ['video/mp4', 'video/x-msvideo', 'video/ogg', 'video/quicktime']
          uploaderFileDescription = question.answer_value
          answer = question.answer_file_key
        } else if (openQuestionType === OPEN_QUESTION_TYPE.ATTACHMENT) {
          uploaderFileDescription = question.answer_value
          answer = question.answer_file_key
        }
        const questionInfo = {
          _question: question,
          id: question.id,
          type: questionType,
          title: question.title,
          isRequired: question.required || false,
          answer,
          surveyType: question.survey_type,
          optionList: _.map(question.options, option => {
            return {
              label: option.description,
              value: option.id
            }
          }),
          uploaderFileTypeList,
          uploaderFileSize,
          uploaderNeedFileDescription: [OPEN_QUESTION_TYPE.VIDEO, OPEN_QUESTION_TYPE.ATTACHMENT].includes(openQuestionType),
          uploaderFileDescription,
          errorMessage: null
        }
        if (openQuestionType === OPEN_QUESTION_TYPE.VIDEO) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.isCandidate ? vm.$t(`${vm.i18nPrefix}.message.uploadTip.videoUploadTipsPC`) : '',
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.openQuestionVideo`, { duration: question.duration })
              ]
            }
          })
        } else if (openQuestionType === OPEN_QUESTION_TYPE.ATTACHMENT) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.attachment`, { size: 200 })
              ]
            }
          })
        }
        return questionInfo
      })
    },
    formatOnboardingQuestion (questionList) {
      const vm = this
      const questionPidMap = {}
      const questionMap = {}
      const questionInfoList = _.map(questionList, question => {
        const onboardingQuestionType = question.type
        const questionType = _.findKey(ONBOARDING_QUESTION_TYPE_MAP, typeList => typeList.includes(onboardingQuestionType))
        const uploaderFileSize = 200 * 1024 * 1024
        const questionInfo = {
          _question: question,
          _parent: null,
          _parentAnswer: null,
          id: question.id,
          type: questionType,
          isRequired: question.is_required,
          answer: question.answer,
          optionList: _.map(question.options, option => addI18nProperty({
            id: option.id,
            pid: option.pid,
            value: option.id
          }, 'label', 'content.cn', 'content.en', option)),
          uploaderFileSize,
          errorMessage: null
        }
        addI18nProperty(questionInfo, 'title', 'title.cn', 'title.en', question)
        if (onboardingQuestionType === ONBOARDING_QUESTION_TYPE.LINK.value) {
          questionInfo.answer = question.answer === '1'
          addI18nProperty(questionInfo, 'policyLink', 'options[0].content.cn', 'options[0].content.en', question)
        } else if (onboardingQuestionType === ONBOARDING_QUESTION_TYPE.ATTACHMENT.value) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.attachment`, { size: 200 })
              ]
            }
          })
        }
        if (questionInfo.type === QUESTION_TYPE.SELECT) {
          questionInfo.answer = parseInt(questionInfo.answer) || null
        }
        questionMap[questionInfo.id] = questionInfo
        const pid = question.pid
        if (pid) {
          const parent = questionMap[pid]
          if (parent) {
            questionInfo._parent = parent
            questionInfo._parentAnswer = parent.answer
          } else {
            let parentChildren = questionPidMap[pid]
            if (!parentChildren) {
              questionPidMap[pid] = parentChildren = []
            }
            parentChildren.push(questionInfo)
          }
          questionInfo._optionList = questionInfo.optionList
          questionInfo._answer = questionInfo.answer
          Object.defineProperty(questionInfo, 'optionList', {
            configurable: true,
            enumerable: true,
            get () {
              const parent = this._parent
              if (!parent.answer) {
                return []
              }
              const parentOption = _.find(parent.optionList, { id: parent.answer })
              return _.filter(this._optionList, { pid: parentOption.id })
            }
          })
          Object.defineProperty(questionInfo, 'answer', {
            configurable: true,
            enumerable: true,
            get () {
              const parent = this._parent
              if (!parent.answer || parent.answer !== this._parentAnswer) {
                return null
              }
              return this._answer
            },
            set (val) {
              const parent = this._parent
              this._parentAnswer = parent.answer
              this._answer = val
            }
          })
        }
        const children = questionPidMap[questionInfo.id]
        if (children) {
          _.forEach(children, child => {
            child._parent = questionInfo
            child._parentAnswer = questionInfo.answer
          })
        }
        return questionInfo
      })
      return questionInfoList
    },
    handleQuestionAnswerChange (questionInfo, answer) {
      if (this.qrImage) {
        questionInfo.errorMessage = null
        return
      }
      if ((!answer || (questionInfo.type === QUESTION_TYPE.CHECKBOX && _.isEmpty(answer))) && questionInfo.isRequired) {
        questionInfo.errorMessage = this.$t(`${this.i18nPrefix}.message.requiredQuestionAnswer`)
      } else {
        questionInfo.errorMessage = null
      }
    },
    async handleFileUpload (questionInfo, file, callback) {
      let errorMessage = null
      if (questionInfo.uploaderFileTypeList && !questionInfo.uploaderFileTypeList.includes(file.type)) {
        errorMessage = this.$t(`${this.i18nPrefix}.message.wrongFileType`)
      } else if (questionInfo.uploaderFileSize && file.size > questionInfo.uploaderFileSize) {
        errorMessage = this.$t(`${this.i18nPrefix}.message.wrongFileSize`)
      }
      if (errorMessage) {
        questionInfo.errorMessage = errorMessage
        callback()
        return
      }
      questionInfo.isUploading = true
      const fileName = file.name
      const ext = fileName.slice(fileName.indexOf('.'))
      const fileKey = `${getFileUID()}${ext}`
      await uploadFile(file, fileKey, false)
      if (this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION) {
        const originQuestion = questionInfo._question
        if (originQuestion.type === OPEN_QUESTION_TYPE.VIDEO) {
          let resp = await this.handleOpenQuestionUploadedVideo({
            file_key: fileKey,
            question_id: originQuestion.id
          })
          const persistentId = resp.persistent_id
          const maxDuration = parseFloat(originQuestion.duration.slice(0, -1))
          if (maxDuration) {
            let duration = resp.duration
            let isFinished = resp.is_finished
            while (!isFinished) {
              resp = await this.getOpenQuestionUploadedVideoDuration({ persistent_id: persistentId })
              duration = resp.duration
              isFinished = resp.is_finished
              await new Promise((resolve, reject) => {
                setTimeout(resolve, 1000)
              })
            }
            if (duration > maxDuration) {
              this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionVideoIsTooLong`, { duration: originQuestion.duration }))
            }
          }
        }
      }
      callback()
      questionInfo.answer = this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION ? fileKey : `${fileKey} ${fileName}`
      this.handleQuestionAnswerChange(questionInfo, questionInfo.answer)
      questionInfo.isUploading = false
    },
    customValidate () {
      let errorQuestion = _.find(this.stepData, 'errorMessage')
      if (!errorQuestion) {
        errorQuestion = _.find(this.stepData, 'isUploading')
        if (errorQuestion) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.uploadTip.waitUploaded`))
        }
      }
      if (errorQuestion) {
        const questionEl = this.$refs[`q_${errorQuestion.id}`][0]
        questionEl.$el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        return false
      }
      return true
    },
    async validate () {
      _.forEach(this.stepData, questionInfo => {
        this.handleQuestionAnswerChange(questionInfo, questionInfo.answer)
      })
      const isValid = await this.customValidate()
      return Boolean(isValid)
    },
    setStepDataToCandidateInfo () {
      // await allUpload
      _.forEach(this.stepData, questionInfo => {
        const originQuestion = questionInfo._question
        if (this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION) {
          originQuestion.answer_value = questionInfo.answer
          if ([OPEN_QUESTION_TYPE.VIDEO, OPEN_QUESTION_TYPE.ATTACHMENT].includes(originQuestion.type)) {
            originQuestion.answer_file_key = questionInfo.answer
            originQuestion.answer_value = questionInfo.uploaderFileDescription
          } else if (originQuestion.type === OPEN_QUESTION_TYPE.CHECKBOX || originQuestion.type === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
            originQuestion.answer_value = _.join(questionInfo.answer)
          } else if (originQuestion.type === OPEN_QUESTION_TYPE.DATE) {
            originQuestion.answer_value = questionInfo.answer ? moment(questionInfo.answer).format('YYYY-MM-DD') : ''
          }
        } else {
          if (originQuestion.type === ONBOARDING_QUESTION_TYPE.LINK.value) {
            originQuestion.answer = questionInfo.answer ? '1' : '0'
          } else {
            originQuestion.answer = questionInfo.answer
          }
        }
      })
    },
    async getStepData () {
      const questionList = _.get(this.candidateInfo, this.infoPropName)
      if (this.showQrImagePropName) {
        const showQrImage = _.get(this.candidateInfo, this.showQrImagePropName)
        if (showQrImage && this.jobId) {
          const baseUrl = process.env.VUE_APP_STYLE_BASE_URL
          let path = null
          if (showQrImage === 'standard') {
            path = MOBILE_OPEN_QUESTION_PATH
          }
          if (showQrImage === 'invite') {
            path = MOBILE_INVITE_QUESTION_PATH
          }
          this.qrImage = `${baseUrl}qrcode-view/?data=${baseUrl}${path}?jobID=${this.jobId}&account=${this.account.id}`
        }
      }
      return this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION ? this.formatOpenQuestion(questionList) : this.formatOnboardingQuestion(questionList)
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-questionnaire-container {
  ::v-deep {
    .el-radio__label {
      vertical-align: top;
      word-break: break-word;
      white-space: normal;
      display: inline-block;
      line-height: 16px;
    }

    .el-checkbox__label {
      vertical-align: top;
      word-break: break-word;
      white-space: normal;
    }
  }
}

.has-resume-view {

  .question-container {
    width: 80%;
    margin-left: 60px;
  }

  ::v-deep {

    .question-container {
      margin-bottom: 20px;
      .question-title-container {
        .question-title {
          font-size: 12px;
        }
      }

      .error-message {
        font-size: 12px;
      }

      .question-answer-wrapper {
        .question-answer {
          .file-status {
            .status {
              font-size: 12px;
            }
          }

          .el-button {
            font-size: 12px;
          }
        }
      }
    }

    .el-form-item__label {
      font-size: 12px;
    }
    .label-inner {
      font-size: 12px;
    }

    .el-textarea__inner {
      font-size: 12px;
    }

    .el-input__inner {
      font-size: 12px;
    }

    .el-radio__label {
      font-size: 12px;
    }

    .el-checkbox__label {
      font-size: 12px;
    }
  }

}

.qrImage-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.qrcode-card {
  width: 200px;
  height: 200px;
  margin: 0 auto;

  .qrcode-container {
    position: relative;
    width: 100%;
    height: 100%;

    .qrcode {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  ::v-deep .el-card__body {
    padding: 0;
  }
}

.qrcode-tip {
  font-size: 14px;
  margin-top: 28px;
  text-align: center;
}

</style>
